import { useEffect, useRef, useState } from 'react';
import { getAuthentication } from '../authenticationManager';
import { getIdCardSignatureStatus, persistIdCardSignature } from '../api';
import { signWithIdCard } from './signWithIdCard';
import { ErrorResponse } from '../apiModels';
import { toSigningErrorResponse } from './signingErrorResponse';
import { SignableEntity } from './types';
import { SigningCancelledByUser } from './signingCancelledByUser';
import { pollForSignatureStatus, startSigningWithChallengeCode } from './signWithChallengeCode';

const POLL_DELAY = 1000;
const SIGNATURE_DONE_STATUS = 'SIGNATURE';

export const useSigning = <TSignableEntity extends { id: number | string }>(
  entityType: SignableEntity,
) => {
  const pollTimeout = useRef<number | null>(null);

  const [challengeCode, setChallengeCode] = useState<string | null>(null);
  const [signingType, setSigningType] = useState<'MOBILE_ID' | 'SMART_ID' | 'ID_CARD' | null>(null);

  const [signed, setSigned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);

  useEffect(() => {
    if (signed) {
      setLoading(false);
      setChallengeCode(null);
    }
  }, [signed]);

  const failWith = (failure: unknown) => {
    setError(toSigningErrorResponse(failure, entityType));
    setSigned(false);
    setLoading(false);
    setChallengeCode(null);
  };

  const startSigning = async (entity: TSignableEntity) => {
    const { signingMethod } = getAuthentication();
    try {
      setLoading(true);
      if (signingMethod === 'ID_CARD') {
        setSigningType('ID_CARD');
        const { signature } = await signWithIdCard<TSignableEntity>(entity, entityType);
        const signatureStatus = await persistIdCardSignature({
          entityId: entity.id.toString(),
          type: entityType,
          signature,
        });
        if (signatureStatus === SIGNATURE_DONE_STATUS) {
          setSigned(true);
        } else {
          pollForIdCard(entity);
        }
      } else if (signingMethod === 'SMART_ID' || signingMethod === 'MOBILE_ID') {
        setSigningType(signingMethod);
        setChallengeCode(
          (await startSigningWithChallengeCode(entity, entityType, signingMethod)) ?? null,
        );
        poll(entity, signingMethod);
      } else {
        throw new Error(`Invalid signing method: ${signingMethod}`);
      }
    } catch (e) {
      if (e instanceof SigningCancelledByUser) {
        cancelSigning();
      } else {
        failWith(e);
      }
    }
  };

  const pollForIdCard = (entity: TSignableEntity) => {
    resetCurrentPolling();

    pollTimeout.current = window.setTimeout(async () => {
      try {
        const signatureStatus = await getIdCardSignatureStatus({
          entityId: entity.id.toString(),
          type: entityType,
        });

        if (signatureStatus === SIGNATURE_DONE_STATUS) {
          setSigned(true);
        } else {
          pollForIdCard(entity);
        }
      } catch (e) {
        failWith(e);
      }
    }, POLL_DELAY);
  };

  const poll = (entity: TSignableEntity, signingMethod: 'MOBILE_ID' | 'SMART_ID') => {
    resetCurrentPolling();

    pollTimeout.current = window.setTimeout(async () => {
      try {
        const signatureStatus = await pollForSignatureStatus(entity, entityType, signingMethod);
        setChallengeCode(signatureStatus.challengeCode);

        if (signatureStatus.statusCode === SIGNATURE_DONE_STATUS) {
          setSigned(true);
        } else {
          poll(entity, signingMethod);
        }
      } catch (e) {
        failWith(e);
      }
    }, POLL_DELAY);
  };

  const resetCurrentPolling = () => {
    if (pollTimeout.current) {
      clearTimeout(pollTimeout.current);
    }
  };

  const cancelSigning = () => {
    resetCurrentPolling();
    setLoading(false);
    setChallengeCode(null);
    setError(null);
  };

  return {
    startSigning,
    cancelSigning,
    signingType,
    signed,
    loading,
    error,
    challengeCode,
  };
};
